import { LANGUAGE_LOCALSTORAGE } from '@host/components/LangSelector'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { FormatIcu } from '@tolgee/format-icu'
import { DevTools, Tolgee, TolgeeProvider } from '@tolgee/react'
import { AdminStoreProvider, createApiClient, WebAppConfigurationApi } from '@tom-ui/admin'
import { BillingServiceStoreProvider } from '@tom-ui/billing'
import { OperationsStoreProvider } from '@tom-ui/operations'
import { PlanningStoreProvider } from '@tom-ui/planning/AppProvider'
import {
  RootStore as StorageRootStore,
  StoreProvider as StorageStoreProvider,
} from '@tom-ui/storage'
import { ThemeProvider } from '@tom-ui/ui'
import {
  AppConfigurationProvider,
  AuthProviderWithHistory,
  UtilsStoreProvider,
} from '@tom-ui/utils'
import { YardStoreProvider } from '@tom-ui/yard'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/en-gb'
import 'dayjs/locale/es'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/ru'
import 'dayjs/locale/uk'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import 'reactflow/dist/style.css'
import { AppContent } from './AppContent'
import deLang from './i18n/de.json'
import enLang from './i18n/en.json'

const defaultLanguage = localStorage.getItem(LANGUAGE_LOCALSTORAGE)

const tolgee = Tolgee()
  .use(DevTools())
  .use(FormatIcu())
  .init({
    language: defaultLanguage ?? 'en',

    // for development
    apiUrl: 'https://app.tolgee.io',
    apiKey: 'tgpak_gi2tsms7gi3ha23gnntdgnzunnrdo4teofrximbtgnsheojynu',

    // for production
    staticData: {
      en: enLang,
      de: deLang,
    },
  })

export const App = () => {
  dayjs.locale(navigator.language)
  dayjs.extend(localizedFormat)

  // Create the storage root store once. `new StorageRootStore()` inline in the
  // JSX would mint a new instance on every render of App, changing the store
  // identity that AppAuthWrapper's post-login effect depends on.
  const [storageStore] = useState(() => new StorageRootStore())

  return (
    <ThemeProvider>
      <AppConfigurationProvider
        getAppConfig={async () =>
          await createApiClient(WebAppConfigurationApi).webAppConfigurationGet()
        }
      >
        <AuthProviderWithHistory>
          <HelmetProvider>
            <Helmet>
              <title>Tom</title>
            </Helmet>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs().locale()}>
              <StorageStoreProvider store={storageStore}>
                <YardStoreProvider>
                  <OperationsStoreProvider>
                    <PlanningStoreProvider>
                      <AdminStoreProvider>
                        <BillingServiceStoreProvider>
                          <UtilsStoreProvider>
                            <TolgeeProvider
                              tolgee={tolgee}
                              fallback='Loading...' // loading fallback
                            >
                              <AppContent />
                            </TolgeeProvider>
                          </UtilsStoreProvider>
                        </BillingServiceStoreProvider>
                      </AdminStoreProvider>
                    </PlanningStoreProvider>
                  </OperationsStoreProvider>
                </YardStoreProvider>
              </StorageStoreProvider>
            </LocalizationProvider>
          </HelmetProvider>
        </AuthProviderWithHistory>
      </AppConfigurationProvider>
    </ThemeProvider>
  )
}
